import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import '@vitesse/base-ui/elements/vitesse-dialog';
import '@vitesse/base-ui/elements/inputs/vitesse-text-input';
import { button, baseButtonStyles } from '@vitesse/base-ui/components/base-buttons';
import { t, LocaleController } from '@vitesse/base-ui/i18n';
import type { IntegrationOp } from '../../../core/domain/integration';
import { validateSubscription, upsertSubscription, removeSubscription } from './topic-subscription-model';

/*
 * <vitesse-topic-subscription-editor> — the "Subscriptions" dialog for a broker
 * integration: the topics it watches by hand, and a small form to add one.
 *
 * WHY THIS EXISTS. A broker cannot be asked what it holds (Kafka excepted), so
 * discovery SAMPLES live traffic for under a second and writes down whatever it
 * overhears. A topic that only publishes when a machine faults, or on an hourly
 * cadence, is therefore invisible — and discovery re-runs only on apply/restore/
 * reconnect, so a thin sample is not a temporary state. Naming a topic registers
 * it regardless of what the sample heard.
 *
 * DUMB by design (components never fetch): it renders `.subs` and emits
 * `subs-change` with the full new list; the viewport owns the apply. Discovered
 * topics are NOT listed here — they are re-derived on every connect, so removing
 * one would be meaningless; only hand-added ones persist.
 *
 * The SUBJECT is optional: leave it blank and the name IS the topic, which is the
 * common case. Fill it in to give a long topic a short readable name.
 */

@customElement('vitesse-topic-subscription-editor')
export class TopicSubscriptionEditor extends LitElement {
  @property({ type: Boolean }) open = false;
  /** The integration's hand-added topics (descriptor.targets). */
  @property({ attribute: false }) subs: IntegrationOp[] = [];
  /** True while the viewport's apply is in flight — locks the form. */
  @property({ type: Boolean }) busy = false;
  /** The server's answer when an apply was refused — already prose. */
  @property() error = '';

  @state() private name = '';
  @state() private subject = '';
  @state() private invalid = '';

  constructor() {
    super();
    new LocaleController(this);
  }

  // This element renders in a SHADOW ROOT, and button() emits only class names —
  // the global base-button.scss cannot cross that boundary, so without
  // baseButtonStyles the Add and remove buttons render as bare browser buttons
  // with a misaligned icon and no border. Light-DOM components must NOT include it.
  static styles = [
    baseButtonStyles,
    css`
    .list {
      display: flex;
      flex-direction: column;
      gap: 4px;
      margin-bottom: 14px;
      max-height: 40vh;
      overflow-y: auto;
    }
    .sub {
      display: flex;
      align-items: center;
      gap: 9px;
      padding: 6px 8px;
      border: 1px solid var(--border-color);
      border-radius: 6px;
      font-size: 13px;
    }
    .sub .name {
      color: var(--text-primary);
      font-family: 'SF Mono', ui-monospace, Menlo, monospace;
      font-size: 12px;
    }
    .sub .subject {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: 11.5px;
      color: var(--text-tertiary, var(--text-secondary));
      font-family: 'SF Mono', ui-monospace, Menlo, monospace;
    }
    .none {
      font-size: 12.5px;
      color: var(--text-secondary);
      margin-bottom: 14px;
    }
    .form {
      display: grid;
      grid-template-columns: 1fr;
      gap: 8px;
      align-items: end;
    }
    label {
      display: block;
      font-size: 11px;
      color: var(--text-secondary);
      margin-bottom: 4px;
    }
    .hint {
      font-size: 11.5px;
      color: var(--text-tertiary, var(--text-secondary));
    }
    .error {
      font-size: 12px;
      color: var(--red, #d64541);
    }
  `,
  ];

  private emit(next: IntegrationOp[]): void {
    this.dispatchEvent(new CustomEvent<IntegrationOp[]>('subs-change', { detail: next, bubbles: true, composed: true }));
  }

  private add(): void {
    const bad = validateSubscription(this.name, this.subject);
    if (bad) {
      this.invalid = bad === 'empty' ? t('integration.subsInvalid') : t('integration.subsWildcard');
      return;
    }
    this.invalid = '';
    this.emit(upsertSubscription(this.subs, this.name, this.subject));
    this.name = '';
    this.subject = '';
  }

  private removeSub(name: string): void {
    this.emit(removeSubscription(this.subs, name));
  }

  protected render() {
    return html`<vitesse-dialog
      .open=${this.open}
      heading=${t('integration.subsManage')}
      width="560px"
      @dialog-close=${() => this.dispatchEvent(new CustomEvent('dialog-close', { bubbles: true, composed: true }))}
    >
      ${
        this.subs.length === 0
          ? html`<div class="none">${t('integration.subsNone')}</div>`
          : html`<div class="list">
              ${this.subs.map(
                (s) => html`<div class="sub">
                  <span class="name">${s.name}</span>
                  <span class="subject" title=${s.spec}>${s.spec ? s.spec : t('integration.subsSameAsName')}</span>
                  ${button({ icon: 'x', color: 'red', size: 'sm', title: t('integration.subsRemove'), disabled: this.busy, onClick: () => this.removeSub(s.name) })}
                </div>`,
              )}
            </div>`
      }
      <div class="form">
        <div>
          <label>${t('integration.subsName')}</label>
          <vitesse-text-input
            size="sm"
            placeholder="factory/line3/press01/temp"
            .value=${this.name}
            ?disabled=${this.busy}
            @change=${(e: CustomEvent<{ value: string }>) => (this.name = e.detail.value)}
            @keydown=${(e: KeyboardEvent) => e.key === 'Enter' && this.add()}
          ></vitesse-text-input>
        </div>
        <div>
          <label>${t('integration.subsSubject')}</label>
          <vitesse-text-input
            size="sm"
            .value=${this.subject}
            ?disabled=${this.busy}
            @change=${(e: CustomEvent<{ value: string }>) => (this.subject = e.detail.value)}
            @keydown=${(e: KeyboardEvent) => e.key === 'Enter' && this.add()}
          ></vitesse-text-input>
        </div>
        <div class="hint">${t('integration.subsHint')}</div>
        ${this.invalid ? html`<div class="error">${this.invalid}</div>` : nothing}
        ${this.error ? html`<div class="error">${this.error}</div>` : nothing}
      </div>
      <span slot="footer"
        >${button({
          label: this.busy ? t('integration.subsSaving') : t('integration.subsAdd'),
          icon: 'plus',
          color: 'blue',
          size: 'sm',
          disabled: this.busy,
          onClick: () => this.add(),
        })}</span
      >
    </vitesse-dialog>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-topic-subscription-editor': TopicSubscriptionEditor;
  }
}
